// QuestionEditorPanel — single composite component that routes by
// question_kind to one of four payload editors (MCQ, TFNG/YNNG, Completion,
// generic JSON for matching variants). Each variant produces:
//   { payload_jsonb, key_pattern } via onChange callbacks.
//
// Per CLAUDE.md "one component per file" guideline, the variant editors are
// kept as internal subcomponents because they are never used outside this
// panel (the same pattern used by SettingsPage's ConfirmModal).

import { useId, useMemo, useState } from 'react';

import type { QuestionKind } from '../../features/admin/tests-api';
import type { Json } from '../../types/database';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Label } from '../ui/Label';

import { matchAnswer } from './answer-key-dsl';
import s from './QuestionEditorPanel.module.css';

interface PanelProps {
  kind: QuestionKind;
  prompt_md: string;
  payload_jsonb: Json;
  key_pattern: string;
  disabled?: boolean | undefined;
  onPromptChange: (next: string) => void;
  onPayloadChange: (next: Json) => void;
  onKeyPatternChange: (next: string) => void;
}

export function QuestionEditorPanel({
  kind,
  prompt_md,
  payload_jsonb,
  key_pattern,
  disabled,
  onPromptChange,
  onPayloadChange,
  onKeyPatternChange,
}: PanelProps): JSX.Element {
  const promptId = useId();
  return (
    <div className={s.root}>
      <div className={s.field}>
        <Label htmlFor={promptId}>Prompt</Label>
        <textarea
          id={promptId}
          className={s.textarea}
          value={prompt_md}
          onChange={(e) => onPromptChange(e.target.value)}
          disabled={disabled}
          rows={3}
        />
      </div>

      <PayloadEditor
        kind={kind}
        payload={payload_jsonb}
        keyPattern={key_pattern}
        onPayloadChange={onPayloadChange}
        onKeyPatternChange={onKeyPatternChange}
        disabled={disabled}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Variant router
// ---------------------------------------------------------------------------

interface VariantProps {
  kind: QuestionKind;
  payload: Json;
  keyPattern: string;
  disabled?: boolean | undefined;
  onPayloadChange: (next: Json) => void;
  onKeyPatternChange: (next: string) => void;
}

function PayloadEditor(props: VariantProps): JSX.Element {
  switch (props.kind) {
    case 'mcq':
      return <McqEditor {...props} />;
    case 'tfng':
      return <TrueFalseEditor variant="tfng" {...props} />;
    case 'ynng':
      return <TrueFalseEditor variant="ynng" {...props} />;
    case 'short_answer':
    case 'form_completion':
    case 'note_completion':
    case 'table_completion':
    case 'summary_completion':
    case 'sentence_completion':
      return <CompletionEditor {...props} />;
    case 'matching_info':
    case 'matching_headings':
    case 'matching_features':
      return <GenericKeyEditor {...props} />;
  }
}

// ---------------------------------------------------------------------------
// MCQ — choices array + key pattern (the canonical answer text)
// ---------------------------------------------------------------------------

interface McqPayload {
  choices: string[];
}

function readChoices(payload: Json): string[] {
  if (typeof payload !== 'object' || payload === null || Array.isArray(payload)) return [];
  const raw = (payload as Record<string, unknown>).choices;
  if (!Array.isArray(raw)) return [];
  return raw.filter((v): v is string => typeof v === 'string');
}

function McqEditor({
  payload,
  keyPattern,
  disabled,
  onPayloadChange,
  onKeyPatternChange,
}: VariantProps): JSX.Element {
  const choices = useMemo(() => readChoices(payload), [payload]);
  const keyId = useId();

  function setChoice(idx: number, value: string): void {
    const next = choices.slice();
    // eslint-disable-next-line security/detect-object-injection -- `idx` comes from our own .map(idx) over `choices`, not user input
    next[idx] = value;
    onPayloadChange({ choices: next } satisfies McqPayload as unknown as Json);
  }

  function addChoice(): void {
    onPayloadChange({ choices: [...choices, ''] } satisfies McqPayload as unknown as Json);
  }

  function removeChoice(idx: number): void {
    const next = choices.filter((_, i) => i !== idx);
    onPayloadChange({ choices: next } satisfies McqPayload as unknown as Json);
  }

  return (
    <div className={s.field}>
      <Label>Choices</Label>
      <ul className={s.choiceList}>
        {choices.map((choice, idx) => (
          <li key={`choice-${String(idx)}`} className={s.choiceRow}>
            <span className={s.choiceLetter} aria-hidden="true">
              {String.fromCharCode(65 + idx)}
            </span>
            <Input
              aria-label={`Choice ${String.fromCharCode(65 + idx)}`}
              value={choice}
              disabled={disabled}
              onChange={(e) => setChoice(idx, e.target.value)}
            />
            <Button
              type="button"
              variant="ghost"
              onClick={() => removeChoice(idx)}
              disabled={disabled || choices.length <= 2}
              aria-label={`Remove choice ${String.fromCharCode(65 + idx)}`}
            >
              ×
            </Button>
          </li>
        ))}
      </ul>
      <Button type="button" variant="ghost" onClick={addChoice} disabled={disabled}>
        + Add choice
      </Button>

      <div className={s.field}>
        <Label htmlFor={keyId}>Correct answer (DSL)</Label>
        <Input
          id={keyId}
          value={keyPattern}
          onChange={(e) => onKeyPatternChange(e.target.value)}
          disabled={disabled}
          placeholder="e.g. B  or  more than tripled"
        />
        <p className={s.hint}>
          Use the exact text from one of the choices, or the option letter (A / B / C / D).
        </p>
      </div>

      <DslPreview keyPattern={keyPattern} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// True/False/Not Given OR Yes/No/Not Given
// ---------------------------------------------------------------------------

const TFNG_OPTIONS = ['true', 'false', 'not given'] as const;
const YNNG_OPTIONS = ['yes', 'no', 'not given'] as const;

function TrueFalseEditor({
  variant,
  keyPattern,
  disabled,
  onKeyPatternChange,
}: VariantProps & { variant: 'tfng' | 'ynng' }): JSX.Element {
  const opts = variant === 'tfng' ? TFNG_OPTIONS : YNNG_OPTIONS;
  const current = keyPattern.trim().toLowerCase();
  const groupName = useId();

  return (
    <div className={s.field}>
      <Label>Correct answer</Label>
      <div role="radiogroup" className={s.radioGroup}>
        {opts.map((opt) => {
          const checked = current === opt;
          return (
            <label key={opt} className={`${s.radioRow} ${checked ? s.radioRowChecked : ''}`}>
              <input
                type="radio"
                name={groupName}
                value={opt}
                checked={checked}
                onChange={() => onKeyPatternChange(opt)}
                disabled={disabled}
                className={s.radioInput}
              />
              <span className={s.radioLabel}>
                {opt
                  .split(' ')
                  .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
                  .join(' ')}
              </span>
            </label>
          );
        })}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Completion + short answer — max_words + DSL key
// ---------------------------------------------------------------------------

interface CompletionPayload {
  max_words?: number;
  task_index?: number;
  part_index?: number;
  prep_seconds?: number;
  record_seconds?: number;
  min_words?: number;
}

function readNum(payload: Json, key: string): number | undefined {
  if (typeof payload !== 'object' || payload === null || Array.isArray(payload)) return undefined;
  // eslint-disable-next-line security/detect-object-injection -- `key` is a literal string passed by our caller, not user input
  const v = (payload as Record<string, unknown>)[key];
  return typeof v === 'number' ? v : undefined;
}

function CompletionEditor({
  payload,
  keyPattern,
  disabled,
  onPayloadChange,
  onKeyPatternChange,
}: VariantProps): JSX.Element {
  const maxWords = readNum(payload, 'max_words') ?? 3;
  const wordsId = useId();
  const keyId = useId();

  function setMaxWords(n: number): void {
    const safe = Math.max(1, Math.min(10, Math.floor(n)));
    const base: CompletionPayload =
      typeof payload === 'object' && payload !== null && !Array.isArray(payload)
        ? (payload as CompletionPayload)
        : {};
    onPayloadChange({ ...base, max_words: safe } as unknown as Json);
  }

  return (
    <>
      <div className={s.field}>
        <Label htmlFor={wordsId}>Max words allowed</Label>
        <Input
          id={wordsId}
          type="number"
          min={1}
          max={10}
          value={maxWords}
          disabled={disabled}
          onChange={(e) => setMaxWords(Number.parseInt(e.target.value, 10) || 1)}
        />
        <p className={s.hint}>IELTS typical: NO MORE THAN TWO/THREE WORDS AND/OR A NUMBER.</p>
      </div>

      <div className={s.field}>
        <Label htmlFor={keyId}>Answer key (Cambridge DSL)</Label>
        <Input
          id={keyId}
          value={keyPattern}
          onChange={(e) => onKeyPatternChange(e.target.value)}
          disabled={disabled}
          placeholder="e.g. (the) sun  ·  detail(s)  ·  12  ·  red|blue"
        />
        <DslSyntaxHelp />
      </div>

      <DslPreview keyPattern={keyPattern} />
    </>
  );
}

// ---------------------------------------------------------------------------
// Matching variants — generic DSL editor + free-form JSON payload escape
// ---------------------------------------------------------------------------

function GenericKeyEditor({
  payload,
  keyPattern,
  disabled,
  onPayloadChange,
  onKeyPatternChange,
}: VariantProps): JSX.Element {
  const keyId = useId();
  const payloadId = useId();
  const serialized = useMemo(() => JSON.stringify(payload, null, 2), [payload]);

  function handlePayload(next: string): void {
    try {
      const parsed = JSON.parse(next) as Json;
      onPayloadChange(parsed);
    } catch {
      // Invalid JSON — keep the current valid payload; user sees the textarea
      // value diverge until they fix it. We don't try to surface a per-keystroke
      // error here because react-hook-form isn't in this editor's loop.
    }
  }

  return (
    <>
      <div className={s.field}>
        <Label htmlFor={keyId}>Answer key (Cambridge DSL)</Label>
        <Input
          id={keyId}
          value={keyPattern}
          onChange={(e) => onKeyPatternChange(e.target.value)}
          disabled={disabled}
          placeholder="e.g. A  ·  v  ·  paragraph c"
        />
        <p className={s.hint}>
          For matching, write the single letter / Roman numeral / label exactly as it should be
          marked. Alternation and optional groups still apply.
        </p>
      </div>

      <details className={s.advanced}>
        <summary>Advanced: payload JSON</summary>
        <div className={s.field}>
          <Label htmlFor={payloadId}>payload_jsonb</Label>
          <textarea
            id={payloadId}
            className={s.textareaCode}
            defaultValue={serialized}
            disabled={disabled}
            rows={6}
            onBlur={(e) => handlePayload(e.target.value)}
          />
          <p className={s.hint}>
            Free-form question payload (paragraph list, heading list, feature labels). Edited as
            JSON until a dedicated Wave 1 editor lands.
          </p>
        </div>
      </details>

      <DslPreview keyPattern={keyPattern} />
    </>
  );
}

// ---------------------------------------------------------------------------
// Tiny live preview using the same matcher Phase 20 will run server-side.
// Lets the SuperAdmin author sanity-check their DSL before saving.
// ---------------------------------------------------------------------------

function DslPreview({ keyPattern }: { keyPattern: string }): JSX.Element {
  const previewId = useId();
  return (
    <div className={s.preview}>
      <Label htmlFor={previewId}>Preview check</Label>
      <PreviewInput id={previewId} keyPattern={keyPattern} />
    </div>
  );
}

function PreviewInput({ id, keyPattern }: { id: string; keyPattern: string }): JSX.Element {
  const [draft, setDraft] = useState('');
  const result = useMemo(
    () => (keyPattern === '' || draft === '' ? null : matchAnswer(draft, keyPattern)),
    [draft, keyPattern],
  );
  return (
    <div className={s.previewRow}>
      <Input
        id={id}
        placeholder="Type a candidate answer to test the DSL"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
      />
      <span
        className={
          result === null ? s.previewBadgeIdle : result.ok ? s.previewBadgeOk : s.previewBadgeFail
        }
        aria-live="polite"
      >
        {result === null ? 'idle' : result.ok ? 'match' : 'no match'}
      </span>
    </div>
  );
}

function DslSyntaxHelp(): JSX.Element {
  return (
    <p className={s.hint}>
      Cambridge DSL: <code>(the)</code> optional, <code>(s)</code> trailing plural,{' '}
      <code>red|blue|green</code> alternation. Digits ↔ words and US ↔ UK spelling normalized.
    </p>
  );
}
